// Markdown -> HTML parser, structured as a per-line+per-fence tokenizer.
//
// Returns a list of blocks. Each block holds the original source slice
// (used as the cache key for diff-rendering in Document) and the
// rendered HTML. Blocks are either single lines or whole fenced code
// blocks. Concatenating block.source over all blocks reproduces the
// input verbatim.
//
// `Document` wraps each block in <span data-block> so the diff render
// can swap one block in place without rebuilding the rest of the
// editor's DOM.

export function parse(content) {
  const blocks = []
  let cursor = 0

  while (cursor < content.length) {
    const block = nextBlock(content, cursor)
    blocks.push(block)
    cursor = block.end
  }

  return blocks
}

function nextBlock(content, start) {
  if (isFenceStart(content, start)) {
    const fenceEnd = findFenceEnd(content, start + 3)
    if (fenceEnd !== null) {
      const source = content.slice(start, fenceEnd)
      return { start, end: fenceEnd, source, html: renderFence(source), isFence: true }
    }
  }
  const newline = content.indexOf("\n", start)
  const end = newline === -1 ? content.length : newline + 1
  const source = content.slice(start, end)
  return { start, end, source, html: renderLine(source), isFence: false }
}

function isFenceStart(content, i) {
  if (content.slice(i, i + 3) !== "```") return false
  return i === 0 || content[i - 1] === "\n"
}

function findFenceEnd(content, from) {
  // Match a closing ``` at the start of a line. Allow trailing
  // whitespace (sources like Typora and Obsidian sometimes emit
  // "```\n``` \n" from a <pre> block, and CRLF endings leave a stray
  // \r before the \n). Include any trailing whitespace + newline so
  // the fence block "owns" its terminating line.
  const closing = /\r?\n```[ \t]*(?:\r?\n|$)/.exec(content.slice(from))
  if (!closing) return null
  return from + closing.index + closing[0].length
}

function renderFence(source) {
  // The fence keeps its leading/trailing ``` markers visible (live editor
  // shows source markdown, not a rendered preview) and the body is HTML
  // escaped. We render the whole thing in one .code span and add
  // data-fence so CSS can style it as a block — inline `.code` styling
  // alone breaks visually across line wraps.
  return `<span class="code" data-fence>${escapeHtml(source.replace(/\n$/, ""))}</span>${source.endsWith("\n") ? "\n" : ""}`
}

function renderLine(source) {
  const hasNewline = source.endsWith("\n")
  let body = hasNewline ? source.slice(0, -1) : source

  body = escapeHtml(body)
  body = applyInlineEmphasis(body)
  body = applyBlockPatterns(body)
  body = applyHr(body)
  body = applyLink(body)
  body = applyImage(body)
  body = applyInlineCode(body)
  body = applyComment(body)

  return hasNewline ? `${body}\n` : body
}

function escapeHtml(text) {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;")
}

function applyInlineEmphasis(text) {
  // Order matters — bold-italic first so triple markers are claimed before
  // the bold/italic passes can split them.
  text = text.replaceAll(/(?<!\*)\*\*\*([^*]+)\*\*\*(?!\*)/g, "<strong><em>***$1***</em></strong>")
  text = text.replaceAll(/(?<!_)___([^_]+)___(?!_)/g, "<strong><em>___$1___</em></strong>")
  text = text.replaceAll(/(?<!\*)\*\*([^*]+)\*\*(?!\*)/g, "<strong>**$1**</strong>")
  text = text.replaceAll(/(?<!_)__([^_]+)__(?!_)/g, "<strong>__$1__</strong>")
  text = text.replaceAll(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>*$1*</em>")
  text = text.replaceAll(/(?<!_)_([^_]+)_(?!_)/g, "<em>_$1_</em>")
  text = text.replaceAll(/~~(.*?)~~/g, "<s>~~$1~~</s>")
  text = text.replaceAll(/==(.*?)==/g, "<mark>==$1==</mark>")
  return text
}

function applyBlockPatterns(text) {
  // Heading: a single regex matching 1–6 leading `#`s.
  text = text.replace(/^(#{1,6}) (.*)$/, (_, hashes, rest) => {
    return `<span class="h${hashes.length}">${hashes} ${rest}</span>`
  })
  // Numbered list item.
  text = text.replace(/^(\d+\.\s.*)$/, '<span class="ol-li">$1</span>')
  // Bullet list item.
  text = text.replace(/^(-\s.*)$/, '<span class="ul-li">$1</span>')
  // Block quote — note `&gt;` because we already HTML-escaped above.
  text = text.replace(/^&gt;(.*)$/, '<span class="quote">>$1</span>')
  return text
}

function applyHr(text) {
  if (text === "---") return '<span class="hr">---</span>'
  return text
}

function applyLink(text) {
  return text.replaceAll(/(?<!!)\[(.*?)\]\((.*?)\)/g, '<span class="link">[$1]($2)</span>')
}

function applyImage(text) {
  return text.replaceAll(/!\[(.*?)\]\((.*?)\)/g, '<span class="img">$&</span>')
}

function applyInlineCode(text) {
  return text.replaceAll(/([^`])`([^`]+)`([^`])/g, '$1<span class="code">`$2`</span>$3')
}

function applyComment(text) {
  return text.replaceAll(/&lt;!--(.*?)--&gt;/g, '<span class="comment">&lt;!--$1--&gt;</span>')
}
